/**
 * /me/end-of-service — M3X.EOS F.18 Employee EOS Hub.
 *
 * Mirrors loans Payment Hub + leaves Leave Hub design discipline.
 * Match screenshot 3: colorful + Solvait-AI assist button +
 * Request An End of Service modal.
 *
 * Pack ref §1.15.
 *
 * Sections:
 *  1. Header band (brand-blue info callout)
 *  2. My EOS Status (active workflow card)
 *  3. Estimated Settlement (bilingual breakdown — gross + deductions + net)
 *  4. End of Service Requests list (past + current with status pills)
 *  5. Quick links (Payment Hub / Travel / Documents / General / Violations)
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "../components/ui";

export function MyEndOfServiceHubPage() {
  const { t } = useTranslation();
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  // Pull the employee's terminations history. The /api/me/eos
  // surface isn't yet wired in M18 for self-service — for now
  // we show an empty state with the Request button.
  const eosQ = useQuery({
    queryKey: ["my-eos"],
    queryFn: async () => {
      // Stub: when /api/me/eos lands, replace this with api.getMyEos()
      return { active: null, history: [] as Array<unknown> };
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "my_eos.title",
          "End of Service Hub",
        )}
        subtitle={t(
          "my_eos.subtitle",
          "Submit, track, and manage your end-of-service request — with Tashghil + Mutaba'aa + Rawatib supporting your case.",
        )}
        actions={
          <button
            type="button"
            onClick={() => setRequestModalOpen(true)}
            className="rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-blue/90"
          >
            {t(
              "my_eos.request_btn",
              "Request An End of Service",
            )}
          </button>
        }
      />

      {/* Brand-blue info callout */}
      <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-5">
        <p className="text-sm text-text-secondary">
          {t(
            "my_eos.info_callout",
            "The End of Service Request page lets you submit resignation, end-of-contract, or retirement requests. Tashghil pre-computes your settlement; Mutaba'aa validates KSA Labor Law floors; Rawatib disburses via off-cycle bank transfer once HR approves.",
          )}
        </p>
      </div>

      {/* Active EOS status (empty until backend wires /api/me/eos) */}
      <section className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="text-base font-semibold text-text-primary">
          {t("my_eos.status_title", "My EOS Status")}
        </h3>
        {!eosQ.data?.active ? (
          <p className="mt-2 text-sm text-text-tertiary">
            {t(
              "my_eos.no_active",
              "You have no active end-of-service request. Click 'Request An End of Service' above to submit one.",
            )}
          </p>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {/* Active workflow card — wired when /api/me/eos lands */}
          </div>
        )}
      </section>

      {/* Estimated Settlement (preview only — pulls from F.15 endpoint
          if the employee opens "What would I get?" — for now placeholder) */}
      <section className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-text-primary">
            {t("my_eos.estimated_settlement", "Estimated Settlement")}
          </h3>
          <span className="text-[10px] text-text-tertiary">
            {t(
              "my_eos.estimate_disclaimer",
              "Final amount may differ based on actual last working day",
            )}
          </span>
        </div>
        <p className="text-xs text-text-tertiary">
          {t(
            "my_eos.estimate_body",
            "Click 'Request An End of Service' to see your real-time estimated settlement broken down by 12 components (gratuity + unpaid salary + leave/ticket encashment + notice period minus loans + violations + deductions).",
          )}
        </p>
      </section>

      {/* EOS requests list */}
      <section className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="mb-3 text-base font-semibold text-text-primary">
          {t("my_eos.requests_title", "End of Service Requests")}
        </h3>
        {eosQ.data?.history.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            {t(
              "my_eos.no_requests",
              "No past requests on record.",
            )}
          </p>
        ) : (
          <div className="space-y-2">
            {/* Wired when /api/me/eos history lands */}
          </div>
        )}
      </section>

      {/* Quick links per screenshot 3 sidebar */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <QuickLink
          to="/me/loans"
          label={t("my_eos.link_payment_hub", "Payment Hub")}
          tone="brand-blue"
        />
        <QuickLink
          to="/me/leaves"
          label={t("my_eos.link_leaves", "My Leaves")}
          tone="brand-teal"
        />
        <QuickLink
          to="/policies/holiday-calendar"
          label={t("my_eos.link_calendar", "Calendar")}
          tone="amber"
        />
        <QuickLink
          to="/me/payment-hub"
          label={t("my_eos.link_payslip", "Payslip")}
          tone="emerald"
        />
        <QuickLink
          to="/operations/eos"
          label={t("my_eos.link_eos_operations", "EOS Operations")}
          tone="brand-purple"
        />
      </section>

      {requestModalOpen && (
        <RequestEosModal onClose={() => setRequestModalOpen(false)} />
      )}
    </div>
  );
}

function QuickLink({
  to,
  label,
  tone,
}: {
  to: string;
  label: string;
  tone: string;
}) {
  const cls =
    tone === "brand-blue"
      ? "border-brand-blue/30 bg-brand-blue/5 text-brand-blue"
      : tone === "brand-teal"
        ? "border-brand-teal/30 bg-brand-teal/5 text-brand-teal"
        : tone === "brand-purple"
          ? "border-brand-purple/30 bg-brand-purple/5 text-brand-purple"
          : tone === "amber"
            ? "border-amber-200 bg-amber-50 text-amber-900"
            : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return (
    <Link
      to={to}
      className={`flex items-center justify-center rounded-lg border p-4 text-center text-sm font-medium ${cls} hover:opacity-80`}
    >
      {label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Request EOS modal — match screenshot 3 right panel (5 accordion sections)
// ---------------------------------------------------------------------------

function RequestEosModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [section, setSection] = useState(1);
  const [type, setType] = useState("resignation");
  const [lastWorkingDay, setLastWorkingDay] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [acknowledgeNotice, setAcknowledgeNotice] = useState(false);
  const [acknowledgeEstimate, setAcknowledgeEstimate] = useState(false);

  const totalSections = 5;
  const sectionsCompleted =
    (lastWorkingDay ? 1 : 0) +
    (notes ? 1 : 0) +
    (reason ? 1 : 0) +
    (acknowledgeNotice ? 1 : 0) +
    (acknowledgeEstimate ? 1 : 0);
  const completionPct = Math.round(
    (sectionsCompleted / totalSections) * 100,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 sm:items-center sm:justify-center">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-xl bg-surface-default p-6 shadow-xl sm:rounded-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-text-primary">
              {t("my_eos.modal_title", "Request An End of Service")}
            </h2>
            <p className="mt-1 text-xs text-text-tertiary">
              {t(
                "my_eos.modal_subtitle",
                "5 sections — fill what applies. Khidmat follows up on missing items.",
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-tertiary hover:text-text-primary"
          >
            ×
          </button>
        </div>

        <div className="mt-4">
          <div className="h-2 w-full rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-brand-blue transition-all"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <p className="mt-1 text-end text-[10px] text-text-tertiary">
            {completionPct}% {t("my_eos.complete", "complete")}
          </p>
        </div>

        {/* Section 1 — Details */}
        <ModalSection
          number={1}
          title={t("my_eos.sec_1", "End of Service Details")}
          expanded={section === 1}
          onToggle={() => setSection(section === 1 ? 0 : 1)}
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">
              {t("my_eos.type_label", "Type of end of service?")}
            </span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full rounded-md border border-border-default p-2 text-sm"
            >
              <option value="resignation">
                {t("my_eos.type_resignation", "Resignation")}
              </option>
              <option value="end_of_contract">
                {t("my_eos.type_eoc", "End of contract")}
              </option>
              <option value="retirement">
                {t("my_eos.type_retirement", "Retirement (if eligible)")}
              </option>
            </select>
          </label>
          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">
              {t("my_eos.lwd_label", "Last Working Day")}
            </span>
            <input
              type="date"
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
              className="w-full rounded-md border border-border-default p-2 text-sm"
            />
          </label>
        </ModalSection>

        {/* Section 2 — Notes + Solvait AI assist */}
        <ModalSection
          number={2}
          title={t("my_eos.sec_2", "Additional Notes")}
          expanded={section === 2}
          onToggle={() => setSection(section === 2 ? 0 : 2)}
        >
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t(
              "my_eos.notes_placeholder",
              "Any additional comments or notes?",
            )}
            className="w-full rounded-md border border-border-default p-2 text-sm"
          />
          <button
            type="button"
            onClick={() =>
              setNotes(
                "Career growth opportunity. Thank you for the experience.",
              )
            }
            className="mt-2 rounded-md border border-brand-purple bg-brand-purple/5 px-3 py-1 text-xs font-medium text-brand-purple hover:bg-brand-purple/10"
          >
            ✦ {t("my_eos.solvait_ai", "Solvait AI suggest")}
          </button>
        </ModalSection>

        {/* Section 3 — Supporting documents */}
        <ModalSection
          number={3}
          title={t("my_eos.sec_3", "Supporting Documents")}
          expanded={section === 3}
          onToggle={() => setSection(section === 3 ? 0 : 3)}
        >
          <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-xs text-text-tertiary">
            {t(
              "my_eos.upload_docs",
              "Drag-drop optional resignation letter or supporting docs",
            )}
          </div>
        </ModalSection>

        {/* Section 4 — Reason */}
        <ModalSection
          number={4}
          title={t("my_eos.sec_4", "Reason")}
          expanded={section === 4}
          onToggle={() => setSection(section === 4 ? 0 : 4)}
        >
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t(
              "my_eos.reason_placeholder",
              "Required for resignation — what's your reason?",
            )}
            className="w-full rounded-md border border-border-default p-2 text-sm"
          />
        </ModalSection>

        {/* Section 5 — Acknowledgements */}
        <ModalSection
          number={5}
          title={t("my_eos.sec_5", "Acknowledgements")}
          expanded={section === 5}
          onToggle={() => setSection(section === 5 ? 0 : 5)}
        >
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={acknowledgeNotice}
              onChange={(e) => setAcknowledgeNotice(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              {t(
                "my_eos.ack_notice",
                "I acknowledge the notice period from my contract starting from submission.",
              )}
            </span>
          </label>
          <label className="mt-2 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={acknowledgeEstimate}
              onChange={(e) => setAcknowledgeEstimate(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              {t(
                "my_eos.ack_estimate",
                "I understand my estimated settlement is subject to final calculation.",
              )}
            </span>
          </label>
        </ModalSection>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
          <button
            type="button"
            className="text-sm text-brand-blue hover:underline"
          >
            {t("my_eos.save_draft", "Save As Draft")}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            >
              {t("my_eos.cancel", "Cancel")}
            </button>
            <button
              type="button"
              disabled={
                !lastWorkingDay ||
                !acknowledgeNotice ||
                !acknowledgeEstimate
              }
              className="rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {t("my_eos.submit", "Submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalSection({
  number,
  title,
  expanded,
  onToggle,
  children,
}: {
  number: number;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 rounded-md border border-slate-200">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between p-3 text-start"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue/10 text-xs text-brand-blue">
            {number}
          </span>
          {title}
        </span>
        <span className="text-text-tertiary">{expanded ? "−" : "+"}</span>
      </button>
      {expanded && <div className="border-t border-slate-200 p-3">{children}</div>}
    </div>
  );
}

export default MyEndOfServiceHubPage;
